import PropTypes from "prop-types"
import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { TextField, Button, InputAdornment } from "@mui/material"
import { MailOutlined, ArrowBack, LockOutlined, CheckCircleOutline } from "@mui/icons-material"
import { get, isEmpty } from "lodash"
import Error from "components/login/form-error"
import Captcha from "components/captcha"
import errorCodes from "helpers/errors"
import useMountEffect from "hooks/mount-effect"
import "./style.scss"

export default function LoginComponent({ user, fetchAuthType, isBasicAuth, login, selectedEmail, reset, errorMessage, setErrorMessage, errorCode, redirect, clearErrorMessage, isRefreshMode, refresh, ssoProvider, refreshSso, requireMfa, requireCaptcha, title, subTitle, userMfaEnabled, setRequireMfa, passwordExpired, serverRedirect }) {
  const navigate = useNavigate()

  useMountEffect(() => {
    // if we get here and we're logged in, move out
    if (!isEmpty(user) && !isRefreshMode) {
      navigate("/")
    }
  })

  const [email, setEmail] = useState()
  const [password, setPassword] = useState()
  const [token, setToken] = useState()
  const [loginErrorMessage] = useState(get(errorCodes, errorCode))
  const [captchaToken, setCaptchaToken] = useState()
  const captchaRef = useRef(null)
  const captchaSiteKey = get(window, "captcha.siteKey")
  const loginDisabled = useMemo(() => {
    return (requireMfa && !token) || (selectedEmail && !password) || (requireCaptcha && !captchaToken)
  }, [requireMfa, token, requireCaptcha, captchaToken, selectedEmail, password])

  useEffect(() => {
    if (errorCode === "0006") {
      window.analytics.track("SSO-registered-but-no-OP-accounts")
    }
  }, [errorCode])

  useEffect(() => {
    if (loginErrorMessage) {
      setErrorMessage(loginErrorMessage)
    }
  }, [loginErrorMessage, setErrorMessage])

  const handleChange = (e) => {
    if (e.target.name === "email") {
      setEmail(e.target.value)
    } else if (e.target.name === "password") {
      setPassword(e.target.value)
    } else if (e.target.name === "token") {
      setToken(e.target.value)
    }
  }

  const checkLoginType = event => {
    event.preventDefault()
    if (email) {
      fetchAuthType(email, redirect || serverRedirect)
      clearErrorMessage()
    }
  }

  const resetCaptcha = () => {
    if (captchaRef.current) {
      captchaRef.current.reset()
      setCaptchaToken()
    }
  }

  const loginUser = event => {
    event.preventDefault()
    clearErrorMessage()

    if (isRefreshMode) {
      if (ssoProvider) {
        refreshSso(selectedEmail)
      } else if (!requireMfa && userMfaEnabled) {
        setRequireMfa(true)
      } else {
        refresh(selectedEmail, password, token, captchaToken)
        setToken()
        setPassword()
        resetCaptcha()
      }
    } else if (selectedEmail && password && (!requireCaptcha || captchaToken)) {
      login(selectedEmail, password, token, redirect, captchaToken, serverRedirect)
      resetCaptcha()
    }
  }

  const goBack = () => {
    reset()
    setEmail()
    setToken()
  }

  const logOut = () => {
    window.location = "/logout"
  }

  const clearError = () => {
    clearErrorMessage()
  }

  const goToForgotPassword = () => {
    reset()
    window.analytics.track("reset-password-link")
    navigate(`/forgot-password?email=${selectedEmail}`)
  }

  let submitButton = "Next"
  if (isRefreshMode && ssoProvider) {
    submitButton = `Sign in using ${ssoProvider}`
  } else if (selectedEmail) {
    submitButton = "Log in"
  }

  let content = <>
    {errorMessage && <Error errorMessage={errorMessage} clearError={clearError} /> }
    <form className="form" onSubmit={selectedEmail ? loginUser : checkLoginType}>
      {!selectedEmail
        && <TextField
          name="email"
          onChange={handleChange}
          placeholder="Email"
          autoFocus
          variant="outlined"
          slotProps={{
            input: {
              startAdornment: <InputAdornment style={{ whiteSpace: "nowrap" }} position="start"><MailOutlined className="textinput-icon" /></InputAdornment>,
            },
          }}
        />}
      {selectedEmail && isBasicAuth && !passwordExpired && !requireMfa && <>
        <TextField
          className="disabled-email"
          name="email"
          disabled
          placeholder="Email"
          variant="outlined"
          value={selectedEmail}
          slotProps={{
            input: {
              startAdornment: <InputAdornment style={{ whiteSpace: "nowrap" }} position="start"><MailOutlined className="textinput-icon" /></InputAdornment>,
            },
          }}
        />
        <TextField
          className="password-box"
          name="password"
          type="password"
          onChange={handleChange}
          autoFocus
          placeholder="Password"
          variant="outlined"
          slotProps={{
            input: {
              startAdornment: <InputAdornment style={{ whiteSpace: "nowrap" }} position="start"><LockOutlined className="textinput-icon" /></InputAdornment>,
            },
          }}
        />
        {!isRefreshMode && !requireMfa && <div className="forgotBox">
          <a className="forgot" onClick={goToForgotPassword}>
            Forgot password?
          </a>
        </div>}
      </>}
      {requireMfa && <TextField
        className="token-box"
        name="token"
        onChange={handleChange}
        autoFocus
        placeholder="Authentication Code"
        variant="outlined"
      />}
      {selectedEmail && requireCaptcha && <Captcha ref={captchaRef} siteKey={captchaSiteKey} onVerify={setCaptchaToken} />}
      <Button type="submit" variant="contained" color="primary" className="login-button" disabled={loginDisabled}>{submitButton}</Button>
      {isRefreshMode && <Button onClick={logOut} variant="contained" color="primary" className="logout-button">Logout</Button>}
    </form>
  </>

  if (passwordExpired) {
    content = <CheckCircleOutline className="check-password-sent" />
  }

  return (
    <div className="login">
      {selectedEmail && !isRefreshMode
        && <div className="back" onClick={goBack}>
          <ArrowBack className="arrow-back" /> Back
        </div>}
      <div className="title">{title}</div>
      <div className="text">{subTitle}</div>
      {content}
    </div>
  )
}

LoginComponent.propTypes = {
  user: PropTypes.object,
  redirect: PropTypes.string,
  errorCode: PropTypes.string,
  selectedEmail: PropTypes.string,
  reset: PropTypes.func.isRequired,
  fetchAuthType: PropTypes.func.isRequired,
  login: PropTypes.func.isRequired,
  isBasicAuth: PropTypes.bool,
  errorMessage: PropTypes.string,
  setErrorMessage: PropTypes.func.isRequired,
  clearErrorMessage: PropTypes.func.isRequired,
  params: PropTypes.object,
  refresh: PropTypes.func.isRequired,
  isRefreshMode: PropTypes.bool,
  ssoProvider: PropTypes.string,
  refreshSso: PropTypes.func.isRequired,
  requireMfa: PropTypes.bool.isRequired,
  requireCaptcha: PropTypes.bool,
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  userMfaEnabled: PropTypes.bool,
  setRequireMfa: PropTypes.func.isRequired,
  passwordExpired: PropTypes.bool.isRequired,
  serverRedirect: PropTypes.string,
}
